import React, { useState } from "react";
import { Button, Badge } from "react-bootstrap";
import BlockyMediumText from "components/custom-texts/blocky-medium-text";

interface NetworkRequest {
  id: string;
  timestamp: string;
  url: string;
  method: string;
  status?: number;
  duration?: number;
  error?: string;
  responseBody?: string;
  requestHeaders?: Record<string, string>;
}

interface NetworkRequestsProps {
  networkRequests: NetworkRequest[];
  onClearRequests: () => void;
}

const NetworkRequests: React.FC<NetworkRequestsProps> = ({
  networkRequests,
  onClearRequests,
}) => {
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button variant="danger" size="sm" onClick={onClearRequests}>
            Clear Requests
          </Button>
        </div>
        <BlockyMediumText style={{ color: "#666", fontSize: "12px" }}>
          Showing requests from when debug page opened
        </BlockyMediumText>
      </div>
      
      <div style={{
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        backgroundColor: "#f8f9fa",
        maxHeight: "400px",
        overflowY: "scroll",
        padding: "10px"
      }}>
        {networkRequests.length === 0 ? (
          <div style={{ color: "#6c757d", textAlign: "center", padding: "20px" }}>
            No network requests yet. Try navigating or refreshing the page.
          </div>
        ) : (
          networkRequests.map((request) => (
            <div key={request.id} style={{
              marginBottom: "12px",
              padding: "12px",
              backgroundColor: "white",
              borderRadius: "6px",
              border: "1px solid #e0e0e0",
              cursor: "pointer"
            }}
            onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
            >
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                marginBottom: "8px",
                flexWrap: "wrap",
                gap: "8px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
                  <Badge 
                    bg={request.method === 'GET' ? 'primary' : request.method === 'POST' ? 'success' : 'secondary'}
                    style={{ fontSize: "10px" }}
                  >
                    {request.method}
                  </Badge>
                  
                  {request.status && (
                    <Badge 
                      bg={request.status < 300 ? 'success' : request.status < 400 ? 'warning' : 'danger'}
                      style={{ fontSize: "10px" }}
                    >
                      {request.status}
                    </Badge>
                  )}
                  {request.error && (
                    <Badge bg="danger" style={{ fontSize: "10px" }}>
                      ERROR
                    </Badge>
                  )}
                  
                  <span style={{ fontSize: "11px", color: "#6c757d" }}>
                    {request.duration ? `${request.duration}ms` : '-'}
                  </span>
                </div>
                
                <span style={{ fontSize: "10px", color: "#6c757d", whiteSpace: "nowrap" }}>
                  {new Date(request.timestamp).toLocaleTimeString()}.{new Date(request.timestamp).getMilliseconds().toString().padStart(3, '0')}
                </span>
              </div>
              
              <div style={{
                fontFamily: "monospace",
                fontSize: "11px",
                color: "#333",
                wordBreak: "break-all",
                backgroundColor: "#f8f9fa",
                padding: "6px 8px",
                borderRadius: "4px",
                marginBottom: expandedRequest === request.id ? "12px" : "0"
              }}>
                {request.url}
              </div>
              
              {expandedRequest === request.id && (
                <div>
                  {request.requestHeaders && Object.keys(request.requestHeaders).length > 0 && (
                    <>
                      <h6 style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "8px", color: "#666" }}>
                        Request Headers:
                      </h6>
                      <div style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        padding: "8px",
                        borderRadius: "4px",
                        fontFamily: "monospace",
                        fontSize: "11px",
                        marginBottom: "12px"
                      }}>
                        {Object.entries(request.requestHeaders).map(([key, value]) => (
                          <div key={key} style={{ marginBottom: "4px" }}>
                            <span style={{ color: "#007bff", fontWeight: "bold" }}>{key}:</span>{' '}
                            <span style={{ color: "#333" }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  
                  <h6 style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "8px", color: "#666" }}>
                    Response Body:
                  </h6>
                  <div style={{
                    backgroundColor: "#000",
                    color: "#00ff00",
                    padding: "10px",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    fontSize: "11px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                  }}>
                    {request.responseBody || '(No response body captured)'}
                  </div>
                  
                  <div style={{ 
                    fontSize: "10px", 
                    color: "#6c757d", 
                    marginTop: "8px",
                    backgroundColor: "#f8f9fa",
                    padding: "6px",
                    borderRadius: "4px"
                  }}>
                    <strong>Debug:</strong> Status: {request.status || 'pending'}, 
                    Duration: {request.duration || 'N/A'}ms, 
                    Headers: {Object.keys(request.requestHeaders || {}).length},
                    Response length: {request.responseBody?.length || 0} chars,
                    Error: {request.error || 'none'}
                  </div>
                </div>
              )}
              
              {expandedRequest === request.id && (
                <div style={{ 
                  fontSize: "10px", 
                  color: "#6c757d", 
                  marginTop: "8px",
                  textAlign: "center"
                }}>
                  Click to collapse
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default NetworkRequests;